import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BlockHighlightOverlay } from './BlockHighlightOverlay';
import { SignatureFieldOverlay, type SignatureField } from './SignatureFieldOverlay';
import type { ReviewBlock, ReviewHighlight } from './adapter/types';

// React.lazy dedupes the dynamic import, so defining these here as well as in
// the orchestrator's finalized-PDF branch is fine. The shared module configures
// the pdf.js worker before either component can mount a <Document>.
const Document = lazy(() => import('./react-pdf-lazy').then((mod) => ({ default: mod.Document })));
const Page = lazy(() => import('./react-pdf-lazy').then((mod) => ({ default: mod.Page })));

type Signatory = { name: string; email: string; role: string; color: string };

interface PdfDocumentLayerProps {
  file: string;
  scale: number;
  /** Rendered page size in CSS pixels at scale 1. */
  pageWidth: number;
  pageHeight: number;
  /**
   * The page's true size in PDF points. Signature fields are stored in points,
   * so the overlay needs these to map them onto the rendered pixels. Omitting
   * them makes the overlay assume US-Letter, which on any other page size both
   * mis-positions fields and mis-clamps dragging — on A4 every field sits ~2.8%
   * off-origin, and the bottom ~50pt of the page becomes undraggable.
   * Defaults keep US-Letter callers unchanged.
   */
  pageWidthPt?: number;
  pageHeightPt?: number;
  templateTitle: string;
  /** This layer's page-element map (one per buffer slot). */
  pageRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
  /** Fires when this layer's PDF finishes parsing, with its page count. */
  onLoaded: (numPages: number) => void;
  /**
   * Fires once every page canvas has actually painted (not just parsed). The
   * parent waits for this before revealing a freshly generated buffer, so the
   * swap never flashes blank pages.
   */
  onReady?: (numPages: number) => void;
  /**
   * Only the visible layer is interactive: it attaches hover/click/touch
   * handlers and renders the block-highlight + signature overlays. The hidden
   * buffer layer renders bare pages just so react-pdf pre-parses/paints them.
   */
  interactive: boolean;

  // Interactive-only wiring (ignored when interactive=false)
  hoveredBlock?: ReviewHighlight | null;
  activeEditBlock?: ReviewHighlight | null;
  /** Transient navigated-to block + nonce that drive the outline-click pulse. */
  flashBlock?: ReviewHighlight | null;
  flashNonce?: number;
  isEditing?: boolean;
  editingBlockId?: string | null;
  signatureFields?: SignatureField[];
  signatories?: Signatory[];
  selectedField?: string | null;
  onSelectField?: (id: string | null) => void;
  onSignatureFieldsChange?: (fields: SignatureField[]) => void;
  onPageClick?: () => void;
  onTextHover?: (e: React.MouseEvent<HTMLDivElement>, page: number) => void;
  onTextLeave?: () => void;
  resolveBlockFromTouch?: (target: HTMLElement, page: number) => ReviewHighlight | null;
  requestBlockSwitch?: (block: ReviewBlock, at: ReviewHighlight | null) => void;
}

/**
 * Renders one PDF "slot" — a react-pdf <Document> plus its page wrappers and
 * (when interactive) the editing overlays. Two of these are stacked by the
 * parent to double-buffer regenerations: the new blob is parsed in a hidden
 * layer, then revealed, so the visible preview never flashes a loader or jumps.
 */
export function PdfDocumentLayer({
  file,
  scale,
  pageWidth,
  pageHeight,
  pageWidthPt = 612,
  pageHeightPt = 792,
  templateTitle,
  pageRefs,
  onLoaded,
  onReady,
  interactive,
  hoveredBlock = null,
  activeEditBlock = null,
  flashBlock = null,
  flashNonce = 0,
  isEditing = false,
  editingBlockId = null,
  signatureFields = [],
  signatories = [],
  selectedField = null,
  onSelectField,
  onSignatureFieldsChange,
  onPageClick,
  onTextHover,
  onTextLeave,
  resolveBlockFromTouch,
  requestBlockSwitch,
}: PdfDocumentLayerProps) {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number | null>(null);
  const noop = () => undefined;

  // Track how many page canvases have painted so we can tell the parent the
  // layer is fully rendered (so it only reveals a freshly-parsed buffer once
  // every page is actually drawn — no blank flash on swap).
  const renderedCountRef = useRef(0);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Reset the paint counter whenever this slot loads a different blob.
  useEffect(() => {
    renderedCountRef.current = 0;
  }, [file]);

  const handlePagePainted = () => {
    renderedCountRef.current += 1;
    if (numPages && renderedCountRef.current >= numPages) {
      onReadyRef.current?.(numPages);
    }
  };

  // Blank, page-sized placeholder — no spinner. Keeps layout stable while a
  // page paints without flashing a loader over the (visible) preview.
  const pagePlaceholder = (
    <div className="bg-white" style={{ width: pageWidth * scale, height: pageHeight * scale }} />
  );

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-20">
          <Loader className="h-8 w-8 animate-spin text-primary" />
        </div>
      }
    >
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }: { numPages: number }) => {
          setNumPages(n);
          onLoaded(n);
        }}
        loading={
          <div className="flex items-center justify-center py-20">
            <Loader className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
        error={
          <div className="flex items-center justify-center py-20">
            <p className="text-destructive font-semibold">
              {t('GENERATEREVIEW_PREVIEW_FAILEDTOLOAD')}
            </p>
          </div>
        }
      >
        <div className="space-y-4">
          {numPages &&
            Array.from({ length: numPages }, (_, i) => i + 1).map((page) => (
              <div
                key={page}
                data-page-number={page}
                ref={(el) => {
                  if (el) {
                    pageRefs.current.set(page, el);
                  } else {
                    pageRefs.current.delete(page);
                  }
                }}
                className="bg-white shadow-xl mx-auto relative rounded-lg"
                style={{ width: pageWidth * scale, height: pageHeight * scale }}
                onClick={interactive ? onPageClick : undefined}
                onMouseMove={interactive && onTextHover ? (e) => onTextHover(e, page) : undefined}
                onMouseLeave={interactive ? onTextLeave : undefined}
                onTouchEnd={
                  interactive
                    ? (e) => {
                        if (selectedField) return;
                        const target = e.target as HTMLElement;
                        if (!target.closest('.react-pdf__Page__textContent span')) return;
                        const resolved = resolveBlockFromTouch?.(target, page);
                        if (!resolved) return;
                        e.preventDefault();
                        if (isEditing && editingBlockId === resolved.block.id) return;
                        requestBlockSwitch?.(resolved.block, resolved);
                      }
                    : undefined
                }
              >
                {interactive && (
                  <BlockHighlightOverlay
                    hoveredBlock={hoveredBlock}
                    activeEditBlock={activeEditBlock}
                    flashBlock={flashBlock}
                    flashNonce={flashNonce}
                    pageNumber={page}
                  />
                )}
                <Page
                  pageNumber={page}
                  scale={scale}
                  width={pageWidth}
                  // Render layers consistently in both slots so flipping
                  // `interactive` never re-renders (and blanks) a painted page.
                  renderTextLayer
                  renderAnnotationLayer
                  loading={pagePlaceholder}
                  onRenderSuccess={handlePagePainted}
                  onRenderError={handlePagePainted}
                />
                {interactive && signatureFields.length > 0 && (
                  <SignatureFieldOverlay
                    fields={signatureFields}
                    signatories={signatories}
                    currentPage={page}
                    scale={scale}
                    pageRef={null}
                    pageElement={pageRefs.current.get(page) || null}
                    pageWidth={pageWidth}
                    pageWidthPt={pageWidthPt}
                    pageHeightPt={pageHeightPt}
                    onFieldsChange={onSignatureFieldsChange ?? noop}
                    selectedField={selectedField}
                    onSelectField={onSelectField ?? noop}
                    onPageClick={onPageClick ?? noop}
                  />
                )}
                {page !== 1 && (
                  <div className="pointer-events-none absolute top-3 right-3 text-globe-grey text-sm font-medium tracking-tight text-right">
                    {templateTitle}
                  </div>
                )}
                {numPages && (
                  <div className="pointer-events-none absolute bottom-3 right-3 text-globe-grey text-sm font-semibold tracking-tight">
                    {t('GENERATEREVIEW_PAGEINDICATOR', { page, total: numPages })}
                  </div>
                )}
              </div>
            ))}
        </div>
      </Document>
    </Suspense>
  );
}
